import styled from '@emotion/styled';
import clsx from 'clsx';

import type { FC, AnchorHTMLAttributes } from 'react';
import type { ButtonVariant, ButtonSize } from './Button';

const StyledLinkButton = styled.a`
  /* Shared styles */
  display: inline-block;
  text-align: center;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontFamilies.body};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  cursor: pointer;
  border: none;
  color: inherit;

  &:focus {
    outline: 4px solid ${({ theme }) => theme.palette.colors?.focus};
  }

  &:disabled {
    cursor: initial;
  }

  /* Class names controlling button size  */
  &.medium {
    min-height: 2.5rem;
    padding: ${({ theme }) => theme.spacing(2, 6)};
    font-size: ${({ theme }) => theme.fontSizes.f2};
    line-height: ${({ theme }) => theme.lineHeights.xl};
  }

  &.large {
    min-height: 3.75rem;
    padding: ${({ theme }) => theme.spacing(3, 6)};
    font-size: ${({ theme }) => theme.fontSizes.f4};
    line-height: ${({ theme }) => theme.lineHeights.xl};
  }

  /* Class names controlling button style based on variant */
  &.primary {
    background-color: ${({ theme }) => theme.palette.colors?.yellow[400]};

    &:hover {
      background-color: ${({ theme }) => theme.palette.colors?.primary};
      color: ${({ theme }) => theme.palette.colors?.white};

      &.inverted {
        background-color: ${({ theme }) => theme.palette.colors?.white};
        color: ${({ theme }) => theme.palette.colors?.primary};
      }
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.colors?.gray[400]};
      background-color: ${({ theme }) => theme.palette.colors?.gray[300]};
    }
  }

  &.secondary {
    background-color: transparent;
    border: 2px solid ${({ theme }) => `${theme.palette.colors?.primary}`};

    &.medium {
      padding: calc(${({ theme }) => theme.spacing(2)} - 2px) ${({ theme }) => theme.spacing(6)};
    }

    &.inverted {
      color: ${({ theme }) => theme.palette.colors?.white};
      border: 2px solid ${({ theme }) => `${theme.palette.colors?.white}`};
    }

    &:hover {
      color: ${({ theme }) => theme.palette.colors?.white};
      background-color: ${({ theme }) => theme.palette.colors?.primary};

      &.inverted {
        color: ${({ theme }) => theme.palette.colors?.primary};
        background-color: ${({ theme }) => theme.palette.colors?.white};
      }
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.colors?.gray[400]};
      border: 2px solid ${({ theme }) => `${theme.palette.colors?.gray[400]}`};
    }
  }

  &.tertiary {
    background-color: transparent;
    padding: ${({ theme }) => theme.spacing(2)};
    font-weight: ${({ theme }) => theme.fontWeights.normal};

    &.medium {
      font-size: ${({ theme }) => theme.fontSizes.f4};
      line-height: ${({ theme }) => theme.lineHeights.xl};
    }

    &.inverted {
      color: ${({ theme }) => theme.palette.colors?.white};
    }

    &:disabled {
      color: ${({ theme }) => theme.palette.colors?.gray[400]};
    }
  }

  &.fullWidth {
    width: 100%;
  }
`;

type CustomButtonProps = {
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  inverted?: boolean;
};

export type LinkButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & CustomButtonProps;

export const LinkButton: FC<LinkButtonProps> = (props) => {
  const { variant, className, size, inverted, fullWidth, ...otherProps } = props;
  const classNames = clsx(className, variant, size, {
    inverted,
    fullWidth,
  });
  return <StyledLinkButton className={classNames} {...otherProps} />;
};

LinkButton.defaultProps = {
  size: 'medium',
  variant: 'primary',
  inverted: false,
};
